import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { router } from '@/app/routes';
import { TOKEN, REFRESH_TOKEN } from '@/utils/constants';
import { refreshTokenApi } from '@/services/auth.service';
import useAuthStore from '@/store/authStore';
import useUIStore from '@/store/uiStore';
import { registerSW } from 'virtual:pwa-register';
import './index.css';

// ──────────────────────────────────────────────
// Auto-reload on new CI/CD deployment
// When Workbox detects a new service worker is ready (skipWaiting + clientsClaim
// means it activates immediately), silently reload the page so users always
// get the latest build without any manual refresh.
// ──────────────────────────────────────────────
registerSW({
  immediate: true,
  onNeedRefresh() {
    // New service worker activated — reload to serve the latest build
    window.location.reload();
  },
  onOfflineReady() {
    // App is ready to work offline (PWA precache complete)
    console.info('[PWA] App ready to work offline.');
  },
});

// ──────────────────────────────────────────────
// Global Error Handling for Vite Preload Failures
// ──────────────────────────────────────────────
// Secondary safety net: if a new version is deployed and the browser tries
// to load an old chunk that no longer exists on the server, reload the page.
window.addEventListener('vite:preloadError', (event) => {
  console.warn('Vite preload error detected. Reloading page...', event);
  window.location.reload();
});

const initializeApp = async () => {
  const token = localStorage.getItem(TOKEN);
  const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);

  if (!token && refreshTokenValue) {
    try {
      const response = await refreshTokenApi(refreshTokenValue);
      const data = response.data;

      if (data?.accessToken && data?.refreshToken) {
        useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
      }
    } catch (error) {
      console.error('Refresh token failed:', error);
      localStorage.clear();
    }
  }
};

const ToasterContainer = () => {
  const isDarkMode = useUIStore((state) => state.isDarkMode);
  return (
    <Toaster
      position="bottom-right"
      richColors
      duration={3000}
      theme={isDarkMode ? 'dark' : 'light'}
    />
  );
};

initializeApp().finally(() => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Failed to find root element');
    return;
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <RouterProvider router={router} />
      <ToasterContainer />
    </React.StrictMode>,
  );
});

